import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  BlogPost,
  PatchAdminV1StoresStoreIdBlogPostsIdBody,
  PostAdminV1StoresStoreIdBlogPostsBody,
} from '@nucleus-commerce/api-client/admin';

import { blogApi } from '../api/blog-api';

import { blogKeys } from './blog-keys';

/** Category post counts follow published posts, so they refetch with the lists. */
function remember(queryClient: ReturnType<typeof useQueryClient>, storeId: string, row: BlogPost) {
  queryClient.setQueryData(blogKeys.postDetail(storeId, row.id), row);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: blogKeys.postLists(storeId) }),
    queryClient.invalidateQueries({ queryKey: blogKeys.categories(storeId) }),
  ]);
}

/** Field errors (handle taken, unknown category) are rendered by the form, not toasted. */
export function useCreateBlogPost(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdBlogPostsBody) => blogApi.createPost(storeId, body),
    meta: { successMessage: 'blog:toasts.postCreated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useUpdateBlogPost(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdBlogPostsIdBody }) =>
      blogApi.updatePost(storeId, id, body),
    meta: { successMessage: 'blog:toasts.postUpdated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useDeleteBlogPost(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => blogApi.removePost(storeId, id),
    meta: { successMessage: 'blog:toasts.postDeleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: blogKeys.postDetail(storeId, id) });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: blogKeys.postLists(storeId) }),
        queryClient.invalidateQueries({ queryKey: blogKeys.categories(storeId) }),
      ]);
    },
  });
}
